<template>
  <div class="tab-grid-wrapper">
    <!-- Toolbar -->
    <div class="filter-row">
      <div class="filter-scroll">
        <div v-for="(f, fi) in ladder.filters" :key="'clf' + fi" class="filter-col">
          <div class="filter-label">{{ f.label }}</div>
          <RmSelect
            v-model="f.value"
            :items="f.items"
            multiple clearable
            :placeholder="f.placeholder"
            placeholder-color="#000000"
            class="filter-select"
          />
        </div>
      </div>
      <div class="filter-actions">
        <div class="settings-anchor">
          <button class="action-button-small" @click="toggleSettings" title="Settings">
            <v-icon small>mdi-cog</v-icon>
          </button>
          <div v-if="settingsOpen" class="settings-backdrop" @click="settingsOpen = false"></div>
          <div v-if="settingsOpen" class="settings-menu" :style="settingsMenuStyle">
            <div class="settings-menu-header">
              <span class="settings-menu-title">Settings</span>
              <v-icon small @click="settingsOpen = false" style="cursor: pointer;">mdi-close</v-icon>
            </div>
            <div class="settings-menu-body">
              <div class="settings-field">
                <div class="filter-label">Ladder</div>
                <RmSelect
                  :items="ladder.columnsAvailable"
                  :value="ladder.ladderProperty.target"
                  itemText="label" itemValue="target"
                  @input="setLadderAxis('ladderProperty', $event)"
                  class="filter-select"
                />
              </div>
              <div class="settings-field">
                <div class="filter-label">Ladder Group</div>
                <RmSelect
                  :items="[emptyColumn, ...ladder.columnsAvailable.filter(k => k.target !== ladder.ladderProperty.target)]"
                  :value="ladder.ladderGroupProperty.target"
                  itemText="label" itemValue="target"
                  @input="setLadderAxis('ladderGroupProperty', $event)"
                  class="filter-select"
                />
              </div>
              <div class="settings-field">
                <label class="filter-label">
                  <input type="checkbox" v-model="ladder.showInvalid" /> Show Invalid Documents
                </label>
              </div>
            </div>
          </div>
        </div>
        <button class="action-button-small" @click="fetchData" title="Refresh">
          <v-icon small>mdi-refresh</v-icon>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="ladder.loading" style="text-align: center; padding: 48px;">
      <div class="loader"></div>
      <span>Loading ladder...</span>
    </div>

    <!-- Empty -->
    <div v-else-if="!ladder.documents.length" style="padding: 24px; color: #888; text-align: center;">
      No documents found.
    </div>

    <!-- Timeline -->
    <div v-else class="timeline-scroll">
      <div class="timeline">
        <div class="timeline-row-title">
          <div class="timeline-side timeline-left"></div>
          <div class="timeline-centre-title">{{ ladder.ladderProperty?.label.split(" ")[0] }}</div>
          <div class="timeline-side timeline-right"></div>
        </div>
        <div
          v-for="(group, gi) in filteredGroups"
          :key="group.name"
          class="timeline-row"
          :class="gi % 2 === 0 ? 'row-left' : 'row-right'"
        >
          <!-- Left content -->
          <div class="timeline-side timeline-left">
            <div v-if="gi % 2 === 0" class="timeline-card">
              <div class="timeline-card-body">
                <div v-for="subgroup in group.sub_groups" :key="subgroup.name" class="timeline-track-group">
                  <div v-if="subgroup.name" class="timeline-doc-track" :style="subgroup.colour ? { borderLeftColor: subgroup.colour } : {}">{{ subgroup.name }}</div>
                  <div
                    v-for="doc in subgroup.docs"
                    :key="doc.doc_id"
                    class="timeline-doc"
                    :title="doc.reference"
                    @click="openDocPopup(doc, $event)"
                    :style="subgroup.colour ? { backgroundColor: subgroup.colour } : {}"
                  >
                    <div class="timeline-doc-name">{{ doc.doc_name }}</div>
                    <div class="timeline-doc-second-line">
                      <div class="timeline-doc-meta">
                        <span v-if="doc.lft_abbreviation" class="timeline-doc-lft">{{ doc.lft_abbreviation }}</span>
                        <span v-else class="timeline-doc-issue" :title="doc.lft_status"><v-icon small :color="doc.lf_level_id_valid && doc.lf_function_id_valid && doc.lf_track_id_valid ? 'red' : 'amber'">mdi-alert</v-icon></span>
                      </div>
                      <span v-if="doc.LF_FAMILY" class="timeline-doc-family">{{ doc.LF_FAMILY }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Centre node -->
          <div class="timeline-centre">
            <div class="timeline-line" v-if="gi > 0"></div>
            <div class="timeline-node" >{{ group.short_name ?? group.name }}</div>
            <div class="timeline-line" v-if="gi < filteredGroups.length - 1"></div>
          </div>

          <!-- Right content -->
          <div class="timeline-side timeline-right">
            <div v-if="gi % 2 !== 0" class="timeline-card">
              <div class="timeline-card-body">
                <div v-for="subgroup in group.sub_groups" :key="subgroup.name" class="timeline-track-group">
                  <div v-if="subgroup.name" class="timeline-doc-track" :style="subgroup.colour ? { borderLeftColor: subgroup.colour } : {}">{{ subgroup.name }}</div>
                  <div
                    v-for="doc in subgroup.docs"
                    :key="doc.doc_id"
                    class="timeline-doc"
                    :title="doc.reference"
                    @click="openDocPopup(doc, $event)"
                    :style="subgroup.colour ? { backgroundColor: subgroup.colour } : {}"
                  >
                    <div class="timeline-doc-name">{{ doc.doc_name }}</div>
                    <div class="timeline-doc-second-line">
                      <div class="timeline-doc-meta">
                        <span v-if="doc.lft_abbreviation" class="timeline-doc-lft">{{ doc.lft_abbreviation }}</span>
                        <span v-else class="timeline-doc-issue" :title="doc.lft_status"><v-icon small :color="doc.lf_level_id_valid && doc.lf_function_id_valid && doc.lf_track_id_valid ? 'red' : 'amber'">mdi-alert</v-icon></span>
                      </div>
                      <span v-if="doc.LF_FAMILY" class="timeline-doc-family">{{ doc.LF_FAMILY }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <DocLevellingPopUp
      :show="docPopup.show"
      :details="docPopup.details"
      :attributes="ladder.columnsAvailable"
      @close="docPopup.show = false"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, watch, toRef } from 'vue';
import RmSelect from '@/components/common/RmSelect.vue';
import DocLevellingPopUp from '@/components/levelling/DocLevellingPopUp.vue';
import axios from "axios";
import utils from "@/common/utils.js";

const emptyColumn = { target: 'none', label: 'None' };

export default {
  name: "LadderView",
  components: { RmSelect, DocLevellingPopUp },
  props: {
    levellingFramework: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const settingsOpen = ref(false);
    const settingsMenuPos = reactive({ top: 0, right: 0 });

    const toggleSettings = (event) => {
      if (settingsOpen.value) {
        settingsOpen.value = false;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      settingsMenuPos.top = rect.bottom + 4;
      settingsMenuPos.right = window.innerWidth - rect.right;
      settingsOpen.value = true;
    };

    const settingsMenuStyle = computed(() => ({
      top: settingsMenuPos.top + 'px',
      right: settingsMenuPos.right + 'px',
    }));

    const ladder = reactive({
      loading: false,
      documents: [],
      filters: [],
      columnsAvailable: [],
      ladderProperty: emptyColumn,
      ladderGroupProperty: emptyColumn,
      showInvalid: false,
    });

    const filteredDocs = computed(() => {
      let docs = ladder.documents;
      const active = ladder.filters.filter(f => f.value?.length);
      if (active.length)
        docs = docs.filter(d => active.every(f => f.value.includes(d[f.target])));
      return ladder.showInvalid ? docs : docs.filter(d => d.lft_abbreviation);
    });

    const groupDocsBy = (docs, column) => {
      if (!column?.target || column.target === emptyColumn.target) return [{ name: '', colour: null, docs }];
      const map = {};
      docs.forEach(d => {
        const key = d[column.target] || '';
        if (!map[key]) {
          const lv = column.valueList.find(x => x.value === key);
          map[key] = { name: lv?.short_name || key, position: lv?.position || 99999999, colour: lv?.background_colour, docs: [] };
        }
        map[key].docs.push(d);
      });
      return Object.values(map);
    };
    const doSort = (a, b) => a.position > b.position ? 1 : a.position < b.position ? -1 : 0;

    const filteredGroups = computed(() => {
      const ladderTarget = ladder.ladderProperty?.target;
      if (!ladderTarget || ladderTarget === emptyColumn.target) return [];

      // Build unique ladder values from filtered docs
      const rows = groupDocsBy(filteredDocs.value, ladder.ladderProperty).filter(r => r.docs.length);
      rows.forEach(r => r.sub_groups = groupDocsBy(r.docs, ladder.ladderGroupProperty).sort(doSort));
      return rows.sort(doSort);
    });

    const fetchData = () => {
      ladder.loading = true;
      axios.get(`document/getLevellingFrameworkDocuments/${props.levellingFramework?.levelling_framework_id}`).then(resp => {
        if (resp.data.Status === 'OK' && resp.data.Data) {
          const data = resp.data.Data;
          ladder.documents = data.documents || [];

          // Build available columns and set defaults
          ladder.columnsAvailable = data.levellingAttributes?.filter(a => a.present) || [];
          const findAttr = (name) => ladder.columnsAvailable.find(a => a.name === name);
          if (!ladder.ladderProperty || ladder.ladderProperty === emptyColumn) {
            ladder.ladderProperty = findAttr('LEVEL_GROUP') || ladder.columnsAvailable[0] || emptyColumn;
          }
          if (!ladder.ladderGroupProperty || ladder.ladderGroupProperty === emptyColumn) {
            ladder.ladderGroupProperty = findAttr('TRACK') || emptyColumn;
          }

          setFilters();
        }
      }).finally(() => { ladder.loading = false; });
    };

    watch(toRef(props, 'levellingFramework'), () => {
      fetchData();
    }, { immediate: true });

    const setLadderAxis = (axis, target) => {
      const prev = ladder[axis];
      if (axis === 'ladderProperty') {
        if (target === ladder.ladderGroupProperty?.target) ladder.ladderGroupProperty = prev;
      } else if (axis === 'ladderGroupProperty') {
        if (target !== emptyColumn.target && target === ladder.ladderProperty?.target) ladder.ladderProperty = prev;
      }
      ladder[axis] = target === emptyColumn.target ? emptyColumn : ladder.columnsAvailable.find(x => x.target === target);

      setFilters();

    };

    const setFilters = () => {
      const axisTargets = [ladder.ladderProperty?.target, ladder.ladderGroupProperty?.target].filter(Boolean);
      ladder.filters = ladder.columnsAvailable
        .filter(a => !axisTargets.includes(a.target))
        .map(a => {
          const existing = ladder.filters.find(f => f.target === a.target);
          return existing || {
            value: null,
            items: a.valueList.map(v => ({ text: v.value, value: v.value })),
            placeholder: `All ${utils.pluralise(a.label)}`,
            label: a.label,
            target: a.target,
          };
        });

    }

    const docPopup = reactive({ show: false, details: [], posX: 0, posY: 0 });

    const openDocPopup = (doc, event) => {
      docPopup.posX = event.clientX;
      docPopup.posY = event.clientY;
      docPopup.show = true;
      const famDocs = ladder.documents.filter(d => d.LF_FAMILY === doc.LF_FAMILY && d.doc_id !== doc.doc_id && !isNaN(d.LF_LEVEL) && !isNaN(doc.LF_LEVEL)).sort((a, b) => Number(a.LF_LEVEL) > Number(b.LF_LEVEL) ? 1 : Number(a.LF_LEVEL) < Number(b.LF_LEVEL) ? -1 : 0 );
      const previous = famDocs.filter(d => Number(d.LF_LEVEL) < Number(doc.LF_LEVEL));
      const next = famDocs.filter(d => Number(d.LF_LEVEL) > Number(doc.LF_LEVEL));
      const current = famDocs.filter(d => Number(d.LF_LEVEL) === Number(doc.LF_LEVEL));
      docPopup.details = [{
        document: doc,
        related: {
          previous: previous,
          next: next,
          current: current,
        }
      }];
    };

    return {
      settingsOpen,
      settingsMenuStyle,
      toggleSettings,
      emptyColumn,
      ladder,
      filteredGroups,
      fetchData,
      setLadderAxis,
      docPopup,
      openDocPopup,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/vars";

.tab-grid-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.filter-row {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  border-radius: 8px;
  padding: 4px 0;
  margin: 0;
  gap: 8px;
}

.filter-scroll {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  gap: 8px;
  align-items: center;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
}

.filter-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-family: 'DM Sans' !important;
  font-weight: 400;
  color: #000000 !important;
  letter-spacing: 0;
  line-height: 140%;
  white-space: nowrap;
}

.filter-select {
  min-width: 180px;
  max-width: 280px;
}

.action-button-small {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 13px;
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 6px;
  cursor: pointer;
  color: #333;
  &:hover {
    background: #f0f0f0;
  }
}

.settings-anchor {
  position: relative;
  display: inline-flex;
}

.settings-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
}

.settings-menu {
  position: fixed;
  z-index: 100;
  background: #F7F9FB;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  width: 280px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.settings-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.settings-menu-title {
  font-size: 14px;
  font-family: 'DM Sans' !important;
  font-weight: 700;
  color: #000000 !important;
}

.settings-menu-body {
  padding: 12px 16px;
  overflow-y: auto;
}

.settings-field {
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
}

.loader {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// --- Timeline ---
.timeline-scroll {
  overflow: auto;
  flex: 1;
  min-height: 0;
  padding: 24px 16px 48px;
}

.timeline {
  display: flex;
  flex-direction: column;
  max-width: 80%;
  margin: 0 auto;
}

.timeline-row {
  display: grid;
  grid-template-columns: 1fr 48px 1fr;
  min-height: 80px;
}
.timeline-row-title {
  display: grid;
  grid-template-columns: 1fr 48px 1fr;
}

// --- Centre spine ---
.timeline-centre, .timeline-centre-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.timeline-centre-title {
  font-family: 'DM Sans' !important;
  font-weight: 700;
  font-size: 11px;
  color: #000000 !important;
}

.timeline-line {
  width: 2px;
  background: #DDDDDD;
  flex: 0 0 auto;
  height: 8px;
}

.timeline-line:last-child {
  flex: 1;
  height: auto;
}

.timeline-node {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #DDDDDD;
  background-color: #EEEEEE;
  flex-shrink: 0;
  z-index: 1;
  font-family: 'DM Sans' !important;
  font-weight: 400;
  font-size: 11px;
  color: #000000 !important;
  line-height: 40px;
  text-align: center;
}

.timeline-node:first-child {
  margin-top: 8px;
}

// --- Side panels ---
.timeline-side {
  display: flex;
  padding: 8px 0;
}

.timeline-left {
  justify-content: flex-end;
  padding-right: 16px;
}

.timeline-right {
  justify-content: flex-start;
  padding-left: 16px;
}

// --- Card ---
.timeline-card {
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #E8EAF6;
  overflow: hidden;
  width: 100%;
  // max-width: 580px;
}

.timeline-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  font-family: 'DM Sans' !important;
}

.timeline-card-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.timeline-card-count {
  font-size: 12px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  padding: 1px 8px;
}

.timeline-card-body {
  padding: 10px 14px 14px;
}

.timeline-levels {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
}

.timeline-level-chip {
  font-size: 11px;
  font-family: 'DM Sans' !important;
  font-weight: 600;
  color: #5C6BC0;
  background: #E8EAF6;
  border-radius: 4px;
  padding: 1px 8px;
}

// --- Document items ---
.timeline-doc {
  display: inline-flex;
  flex-direction: column;
  padding: 6px 10px;
  margin-right: 8px;
  margin-bottom: 8px;
  border-radius: 8px;
  border: 1px solid #DDDDDD;
  transition: background-color 0.15s;
  cursor: pointer;

  &:hover {
    background: #EEF0F8;
  }
}

.timeline-doc-name {
  font-size: 13px;
  font-family: 'DM Sans' !important;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.timeline-doc-second-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.timeline-doc-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.timeline-doc-family {
  font-size: 11px;
  font-family: 'DM Sans' !important;
  color: #888;
  white-space: nowrap;
}

.timeline-doc-lft {
  font-size: 11px;
  font-weight: 700;
  font-family: 'DM Sans' !important;
  color: #333;
  border: 1px solid #BDBDBD;
  border-radius: 4px;
  padding: 0 4px;
  white-space: nowrap;
}

.timeline-doc-issue {
  display: inline-flex;
  align-items: center;
}

.timeline-track-group {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  & + .timeline-track-group {
    margin-top: 10px;
  }
}

.timeline-doc-track {
  width: 100%;
  font-size: 12px;
  font-family: 'DM Sans' !important;
  font-weight: 600;
  color: #555;
  padding: 2px 0 4px;
  border-left: 3px solid #C5CAE9;
  padding-left: 8px;
}

.timeline-empty {
  font-size: 13px;
  color: #999;
  padding: 8px 0;
  text-align: center;
}
</style>
